import { X, LogOut, BookText, Trash2, Megaphone, MessageSquare, MessageCircleQuestion, UserCog, ChevronRight, Sparkle } from "lucide-react";
import MenuAccountRow from "./MenuAccountRow";
import { useReleaseNotes } from "@/hooks/useReleaseNotes";
import TenantSwitchSheet from "./TenantSwitchSheet";
import { canSwitchTenant } from "@/utils/tenantSwitch";
import { Link, useNavigate } from "react-router";
import { useEffect, useRef, useState } from "react";
import type React from "react";
import type { LucideIcon } from "lucide-react";
import {
  ROUTE_ADMIN,
  ROUTE_ANNOUNCEMENTS,
  ROUTE_DICTIONARIES,
  ROUTE_GUIDE,
  ROUTE_INQUIRIES,
  ROUTE_SETTINGS,
  ROUTE_TRASH,
} from "@/utils/const";
import { handleLogout } from "@/utils/logout";
import { checkRoleIsAdmin } from "@/utils/role";
import type { WhoamiMe } from "@shared-domain/auth-service/contract";

interface MenuSheetProps {
  isOpen: boolean;
  onClose: () => void;
  auth: WhoamiMe;
}

// アニメーション定数（旧 MenuModal と統一）
const MENU_DURATION_MS = 600;
const MENU_EASING = "cubic-bezier(0.03, 0.95, 0.09, 1)";

const Row: React.FC<{
  icon: LucideIcon;
  label: string;
  danger?: boolean;
  onClick?: () => void;
  to?: string;
  /** PRODUCT_DEV-762: 未読を知らせる赤ポチ（件数は出さない）。 */
  dot?: boolean;
}> = ({ icon: Icon, label, danger, onClick, to, dot }) => {
  const iconColor = danger ? "text-danger" : "text-primary";
  const labelColor = danger ? "text-danger" : "text-neutral-900";
  const className =
    "flex items-center gap-3.5 px-1 py-3 w-full text-left bg-transparent border-0 cursor-pointer hover:bg-neutral-100 rounded-md transition-colors";
  const inner = (
    <>
      <div className="w-7 h-7 flex items-center justify-center flex-none">
        <Icon size={20} className={iconColor} />
      </div>
      <span className={`text-[15px] font-medium ${labelColor} flex-1`}>{label}</span>
      {dot && (
        <span
          className="w-2 h-2 rounded-full bg-danger flex-none"
          aria-label="未読あり"
          role="status"
        />
      )}
      <ChevronRight size={14} className="text-neutral-400 flex-none" />
    </>
  );
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider px-1 pt-3.5 pb-1">
    {children}
  </div>
);

export const MenuSheet: React.FC<MenuSheetProps> = ({ isOpen, onClose, auth }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  // PRODUCT_DEV-631: テナント切替シート（Umeee の admin + sysadmin のみ）
  const [isTenantSwitchOpen, setIsTenantSwitchOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();
  // PRODUCT_DEV-762: 未読のリリースノート（Minizo ビルドは RELEASE_NOTES が空なので常に false）
  const { hasUnread: hasUnreadRelease } = useReleaseNotes();

  useEffect(() => {
    if (isOpen) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      setIsMounted(true);
      const id = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(id);
    }
    setIsVisible(false);
    closeTimeoutRef.current = setTimeout(() => {
      setIsMounted(false);
      closeTimeoutRef.current = null;
    }, MENU_DURATION_MS);
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // PRODUCT_DEV-631: メニューの開閉が切り替わったら切替シートは畳む。
  // 開いたまま閉じると、次にメニューを開いた瞬間に切替シートが復帰してしまう。
  // （effect で setState すると余分な再レンダーになるため、React 公式の
  //   「props 変化時に描画中へ state を合わせる」パターンを使う）
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (isTenantSwitchOpen) setIsTenantSwitchOpen(false);
  }

  if (!isMounted) return null;

  const isAdmin = checkRoleIsAdmin(auth);
  const showTenantSwitch = canSwitchTenant(auth);

  const handleSettingsClick = () => {
    onClose();
    navigate(ROUTE_SETTINGS);
  };

  return (
    <>
      {/* バックドロップ */}
      <div
        className={`fixed inset-0 bg-black z-50 transition-opacity ${
          isVisible ? "opacity-50" : "opacity-0"
        }`}
        style={{ transitionDuration: `${MENU_DURATION_MS}ms`, transitionTimingFunction: MENU_EASING }}
        onClick={onClose}
      />
      {/* シート本体 */}
      <div
        className={`fixed inset-x-0 bottom-0 bg-white z-50 rounded-t-3xl pb-safe-bottom transition-transform max-h-[88vh] flex flex-col ${
          isVisible ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ transitionDuration: `${MENU_DURATION_MS}ms`, transitionTimingFunction: MENU_EASING }}
      >
        {/* ヘッダー */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2 flex-none">
          <h2 className="text-lg font-bold text-neutral-900">メニュー</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-neutral-100 transition-colors"
            aria-label="閉じる"
          >
            <X size={18} className="text-neutral-700" />
          </button>
        </div>

        {/* アカウント行 (基本設定ピル / PRODUCT_DEV-631 テナント切替) */}
        <MenuAccountRow
          auth={auth}
          onSettings={handleSettingsClick}
          onSwitchTenant={
            showTenantSwitch ? () => setIsTenantSwitchOpen(true) : undefined
          }
        />

        {/* セクション群 */}
        <div className="flex-1 overflow-y-auto px-5 pb-4">
          <SectionLabel>ライブラリ</SectionLabel>
          {/* PRODUCT_DEV-583 R5/614: 顧客マスタ導線は「顧客一覧」タブに統合済み（100%反映）。
              マイページ/顧客マスタ/担当の引き継ぎ/通知の導線はメニューに出さない
              （通知はヘッダーのベル、引き継ぎは顧客一覧ケバブから到達可能）。 */}
          <Row icon={BookText} label="辞書" to={ROUTE_DICTIONARIES} onClick={onClose} />
          <Row icon={Trash2} label="ゴミ箱" to={ROUTE_TRASH} onClick={onClose} />

          <SectionLabel>インフォメーション</SectionLabel>
          {/* PRODUCT_DEV-762: 未読のリリースノートがあることを赤ポチで知らせる。
              使い方ガイドにも「メニューの『ニュース』項目にバッジが表示される」と書いてある。 */}
          <Row
            icon={Megaphone}
            label="ニュース"
            to={ROUTE_ANNOUNCEMENTS}
            onClick={onClose}
            dot={hasUnreadRelease}
          />
          <Row icon={MessageSquare} label="使い方ガイド" to={ROUTE_GUIDE} onClick={onClose} />
          <Row icon={MessageCircleQuestion} label="お問い合わせ" to={ROUTE_INQUIRIES} onClick={onClose} />

          {isAdmin && (
            <>
              <div className="mt-4 -mx-5 px-5 pt-3 border-t border-neutral-200">
                <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-wider flex items-center gap-1.5 pb-1">
                  <Sparkle size={10} className="text-neutral-500" />
                  管理者
                </div>
              </div>
              <Row icon={UserCog} label="管理者画面" to={ROUTE_ADMIN} onClick={onClose} danger />
            </>
          )}

          <div className="h-px bg-neutral-200 my-3.5" />
          <Row icon={LogOut} label="ログアウト" onClick={handleLogout} danger />
        </div>
      </div>

      {/* PRODUCT_DEV-631: メニュー(z-50)より前面に開く。
          開いている間だけマウントし、閉じたら絞り込み・エラー state ごと破棄する */}
      {showTenantSwitch && isTenantSwitchOpen && (
        <TenantSwitchSheet
          isOpen
          onClose={() => setIsTenantSwitchOpen(false)}
          auth={auth}
        />
      )}
    </>
  );
};

export default MenuSheet;
